import { useMemo } from 'react';
import type { WidenMediaElement } from '@hub/libs/widen-utils';
import { MediaElementType } from '@hub/libs/widen-utils';
import type { IbhMaterialLightResponseDto, IbhWidenDto } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhWidenDtoTypeEnum } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import type { ShapeDescriptor } from '@hub/bearing-hub/common/types';

const MARKETING_BADGE_LIST = {
  pfasFree: 'pjcxxtsqjs',
};

const mapArticleWidenTypeToMediaElementType = (mediaType: IbhWidenDtoTypeEnum): MediaElementType | undefined => {
  if (mediaType === 'image') {
    return MediaElementType.IMAGE;
  }
  if (mediaType === 'drawing') {
    return MediaElementType.DRAWING;
  }
  return undefined;
};

type UseProductMediaParams = {
  widens: IbhWidenDto[];
  title: string;
  widenShortKey: string;
  shape: ShapeDescriptor;
  material?: IbhMaterialLightResponseDto;
};

export const useProductMedia = ({
  widens,
  title,
  widenShortKey,
  material,
  shape,
}: UseProductMediaParams): WidenMediaElement[] => {
  return useMemo(() => {
    const marketingBadge = material?.PFASFree ? MARKETING_BADGE_LIST.pfasFree : '';
    const mediaList = widens.filter(
      widen => widen.type === IbhWidenDtoTypeEnum.Image || widen.type === IbhWidenDtoTypeEnum.Drawing
    );
    switch (mediaList.length) {
      case 0:
        return [
          {
            type: MediaElementType.IMAGE,
            filename: widenShortKey,
            shortCode: widenShortKey,
            description: title,
            marketingBadge,
          },
        ];
      case 1:
        return [
          {
            type: MediaElementType.IMAGE,
            filename: widenShortKey,
            shortCode: widenShortKey,
            description: title,
            marketingBadge,
          },
          {
            type: MediaElementType.DRAWING,
            filename: shape.widenShortKeySchema,
            shortCode: shape.widenShortKeySchema,
            description: `${title} drawing`,
          },
        ];
      default:
        return mediaList.map(data => ({
          type: mapArticleWidenTypeToMediaElementType(data.type),
          filename: data.externalId,
          shortCode: data.externalId,
          marketingBadge,
          description:
            mapArticleWidenTypeToMediaElementType(data.type) === MediaElementType.DRAWING ? `${title} drawing` : title,
        }));
    }
  }, [widens, title, widenShortKey, material, shape]);
};
